import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAlertTriangle, FiInfo, FiShield, FiScale } = FiIcons;

const SectionTitle = ({ children, number }) => (
  <h2 className="text-xl sm:text-2xl font-bold text-white mt-12 mb-6 flex items-baseline gap-3 group">
    <span className="text-blue-500 font-mono text-lg sm:text-xl opacity-80 group-hover:opacity-100 transition-opacity">
      {number}.
    </span>
    {children}
  </h2>
);

const SubTitle = ({ children }) => (
  <h3 className="text-lg font-semibold text-gray-200 mt-8 mb-4">
    {children}
  </h3>
);

const Paragraph = ({ children }) => (
  <p className="text-gray-400 leading-relaxed mb-4 text-[15px] sm:text-base">
    {children}
  </p>
);

const BulletList = ({ items }) => (
  <ul className="space-y-3 mb-6 ml-1 sm:ml-4">
    {items.map((item, index) => (
      <li key={index} className="flex items-start gap-3 text-gray-400 text-[15px] sm:text-base">
        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
        <span dangerouslySetInnerHTML={{ __html: item }} />
      </li>
    ))}
  </ul>
);

export default function Terms() {
  return (
    <div className="relative w-full overflow-hidden">
      {/* Background Effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-blue-900/10 rounded-full blur-[120px] -z-10 pointer-events-none"></div>
      <div className="absolute top-[40%] right-0 w-[600px] h-[600px] bg-purple-900/5 rounded-full blur-[100px] -z-10 pointer-events-none"></div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium mb-6">
            <SafeIcon icon={FiScale} />
            Legal Documentation
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mb-6">
            Terms and Conditions
          </h1>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-gray-500 text-sm md:text-base">
            <span>Effective Date: June 1, 2025</span>
            <span className="hidden sm:inline">•</span>
            <span>Last Updated: June 1, 2025</span>
          </div>
        </motion.div>

        {/* Content Container */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-[#111827]/80 backdrop-blur-xl border border-gray-800/60 rounded-2xl md:rounded-3xl p-6 sm:p-10 md:p-14 shadow-2xl shadow-black/40"
        >
          
          <div className="prose prose-invert max-w-none">
            <Paragraph>
              Please read these Terms and Conditions ("Terms", "Agreement") carefully before accessing or using the ProfitLense.AI platform, website, tools, indicators, or any related services (collectively, the "Service") operated by ProfitLense.AI ("Company", "we", "us", or "our").
            </Paragraph>
            <Paragraph>
              By accessing or using our Service, you confirm that you are at least 18 years of age, have read and understood these Terms, and agree to be legally bound by them. If you do not agree to any part of these Terms, you must immediately discontinue use of the Service.
            </Paragraph>

            <SectionTitle number="1">Nature of the Service</SectionTitle>
            <Paragraph>
              ProfitLense.AI is an educational and analytical software platform that provides AI-powered trading indicators, strategy tools, and market data analytics integrated with TradingView. The Service is designed strictly for educational and informational purposes.
            </Paragraph>

            <div className="my-8 bg-blue-900/10 border border-blue-500/20 rounded-xl overflow-hidden">
              <div className="bg-blue-500/10 px-6 py-3 border-b border-blue-500/20 flex items-center gap-2">
                <SafeIcon icon={FiInfo} className="text-blue-400" />
                <span className="font-bold text-blue-300 tracking-wide text-sm uppercase">Important Notice</span>
              </div>
              <div className="p-6">
                <BulletList items={[
                  "<strong>ProfitLense.AI is NOT a registered investment advisor</strong>, broker-dealer, financial planner, or commodity trading advisor under U.S. law, including the Investment Advisers Act of 1940 or the Commodity Exchange Act.",
                  "Nothing provided through our Service constitutes investment advice, financial advice, trading advice, legal advice, or any other professional advice.",
                  "All content, signals, indicators, alerts, and tools are for educational and analytical purposes only.",
                  "Past performance of any trading strategy, indicator, or algorithm does not guarantee future results.",
                  "Trading in stocks, futures, options, forex, and cryptocurrency involves substantial risk of loss and is not suitable for all investors."
                ]} />
              </div>
            </div>

            <SectionTitle number="2">Risk Disclaimer</SectionTitle>
            <Paragraph>
              Trading and investing in financial markets involves a high degree of risk. You should carefully consider whether trading is appropriate for you in light of your financial condition, risk tolerance, and investment objectives.
            </Paragraph>
            <div className="my-6 bg-red-900/10 border-l-4 border-red-500/50 p-6 rounded-r-xl">
              <BulletList items={[
                "You may lose all or more than your initial investment.",
                "Leveraged trading products can result in losses exceeding your deposited funds.",
                "Market conditions, volatility, and unforeseen events can cause significant financial losses.",
                "The use of our indicators and tools does not guarantee profitable trades or investment outcomes."
              ]} />
              <p className="text-red-200/80 text-sm mt-4 italic">
                By using ProfitLense.AI, you acknowledge that you understand these risks and accept full responsibility for any trading or investment decisions you make. We strongly recommend consulting a licensed financial advisor or broker before making any trading decisions.
              </p>
            </div>

            <SectionTitle number="3">Eligibility</SectionTitle>
            <Paragraph>To use ProfitLense.AI, you must:</Paragraph>
            <BulletList items={[
              "Be at least 18 years of age.",
              "Be a legal resident or citizen of the United States or any jurisdiction where the use of such services is lawful.",
              "Not be barred from receiving services under any applicable law.",
              "Have a valid TradingView account (free or paid) to access indicator integrations."
            ]} />
            <Paragraph>
              We reserve the right to refuse service, terminate accounts, or cancel subscriptions at our sole discretion if we determine that any user is in violation of these Terms or applicable laws.
            </Paragraph>

            <SectionTitle number="4">Subscription Plans and Payments</SectionTitle>
            <SubTitle>4.1 Subscription Fees</SubTitle>
            <Paragraph>
              Access to ProfitLense.AI is provided on a subscription basis. Subscription plans, pricing, and features are described on our website and are subject to change with prior notice.
            </Paragraph>
            
            <SubTitle>4.2 Billing</SubTitle>
            <BulletList items={[
              "All payments are processed securely through our authorized payment processors.",
              "Subscriptions are billed in advance on a monthly or annual basis depending on the plan selected.",
              "You authorize us to charge your payment method on a recurring basis until you cancel your subscription."
            ]} />

            <SubTitle>4.3 Refund Policy</SubTitle>
            <BulletList items={[
              "Due to the digital and immediate-access nature of our Service, all sales are generally final.",
              "Refunds may be considered on a case-by-case basis within 7 days of the initial purchase if the Service was not accessible due to a technical fault on our end.",
              "No refunds will be issued for partial billing periods, unused subscription time, or dissatisfaction with trading results.",
              "To request a refund, contact us at support@profitlense.ai within the eligible period."
            ]} />

            <SubTitle>4.4 Cancellation</SubTitle>
            <Paragraph>
              You may cancel your subscription at any time through your account dashboard. Cancellation will take effect at the end of your current billing period, and you will continue to have access to the Service until then.
            </Paragraph>

            <SectionTitle number="5">User Accounts</SectionTitle>
            <BulletList items={[
              "You are responsible for maintaining the confidentiality of your account credentials.",
              "You agree to notify us immediately of any unauthorized access or use of your account.",
              "You are solely responsible for all activity that occurs under your account.",
              "You may not share, transfer, or sell your account access to any third party.",
              "Creating multiple accounts to circumvent restrictions or obtain unauthorized access is strictly prohibited."
            ]} />

            <SectionTitle number="6">Intellectual Property</SectionTitle>
            <Paragraph>
              All content, materials, software, algorithms, indicators, branding, logos, graphics, and other intellectual property made available through ProfitLense.AI are the exclusive property of ProfitLense.AI or its licensors and are protected by U.S. and international copyright, trademark, and intellectual property laws.
            </Paragraph>
            <Paragraph>
              You are granted a limited, non-exclusive, non-transferable, revocable license to access and use the Service solely for your personal, non-commercial use in accordance with these Terms. You may not:
            </Paragraph>
            <BulletList items={[
              "Copy, reproduce, distribute, or publicly display any part of the Service without prior written consent.",
              "Reverse-engineer, decompile, or attempt to extract the source code of our algorithms or software.",
              "Use our branding, trademarks, or proprietary materials for commercial purposes.",
              "Share, resell, or sublicense your access to the platform."
            ]} />

            <SectionTitle number="7">Prohibited Conduct</SectionTitle>
            <Paragraph>By using ProfitLense.AI, you agree not to:</Paragraph>
            <BulletList items={[
              "Violate any applicable federal, state, or local laws or regulations.",
              "Use the Service to manipulate financial markets or engage in fraudulent trading activities.",
              "Attempt to gain unauthorized access to our systems, servers, or databases.",
              "Transmit any malware, viruses, or harmful code through the Service.",
              "Harass, threaten, or harm other users or our staff.",
              "Use automated tools, bots, or scrapers to access or interact with our platform.",
              "Misrepresent your identity or affiliation with any person or entity."
            ]} />

            <SectionTitle number="8">Third-Party Services</SectionTitle>
            <Paragraph>
              ProfitLense.AI integrates with third-party platforms, including TradingView. Your use of these third-party services is governed by their respective terms of service and privacy policies. We are not responsible for the availability, accuracy, or performance of any third-party services.
            </Paragraph>
            <Paragraph>
              Links to third-party websites or services from our platform are provided for convenience only and do not constitute an endorsement of those services.
            </Paragraph>

            <SectionTitle number="9">Disclaimers and Limitation of Liability</SectionTitle>
            <SubTitle>9.1 No Warranty</SubTitle>
            <div className="p-4 bg-gray-900/50 border border-gray-700 rounded-lg mb-6">
              <p className="text-gray-300 text-sm uppercase tracking-wider font-semibold leading-relaxed">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, OR NON-INFRINGEMENT.
              </p>
            </div>
            <Paragraph>We do not warrant that:</Paragraph>
            <BulletList items={[
              "The Service will be uninterrupted, error-free, or free from technical issues.",
              "Any information or analysis provided will be accurate, complete, or timely.",
              "The use of our indicators will result in profitable trades."
            ]} />

            <SubTitle>9.2 Limitation of Liability</SubTitle>
            <div className="p-4 bg-gray-900/50 border border-gray-700 rounded-lg mb-6">
              <p className="text-gray-300 text-sm uppercase tracking-wider font-semibold leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, PROFITLENSE.AI, ITS OFFICERS, DIRECTORS, EMPLOYEES, AFFILIATES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE, INCLUDING BUT NOT LIMITED TO:
              </p>
            </div>
            <BulletList items={[
              "Loss of profits, revenue, or trading capital.",
              "Loss of data or business opportunities.",
              "Any trading decisions made based on our tools, indicators, or content."
            ]} />
            <Paragraph>
              Our total liability to you for any claims arising from these Terms or the Service shall not exceed the amount you paid to us in the three (3) months preceding the claim.
            </Paragraph>

            <SectionTitle number="10">Indemnification</SectionTitle>
            <Paragraph>
              You agree to indemnify, defend, and hold harmless ProfitLense.AI and its officers, directors, employees, agents, and licensors from and against any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising from:
            </Paragraph>
            <BulletList items={[
              "Your use or misuse of the Service.",
              "Your violation of these Terms.",
              "Your violation of any applicable law or regulation.",
              "Any trading or investment decisions you make based on our Service."
            ]} />

            <SectionTitle number="11">Privacy Policy</SectionTitle>
            <Paragraph>
              Your use of ProfitLense.AI is also governed by our Privacy Policy, which is incorporated by reference into these Terms. Our Privacy Policy explains how we collect, use, and protect your personal information in compliance with applicable U.S. privacy laws, including the California Consumer Privacy Act (CCPA) where applicable.
            </Paragraph>
            <Paragraph>
              Please review our Privacy Policy at <a href="#" className="text-blue-400 hover:text-blue-300 underline decoration-blue-500/30 underline-offset-4">profitlense.ai/privacy-policy</a>.
            </Paragraph>

            <SectionTitle number="12">Governing Law and Dispute Resolution</SectionTitle>
            <SubTitle>12.1 Governing Law</SubTitle>
            <Paragraph>
              These Terms shall be governed by and construed in accordance with the laws of the United States and the State of Delaware, without regard to its conflict of law provisions.
            </Paragraph>
            <SubTitle>12.2 Arbitration Agreement</SubTitle>
            <Paragraph>
              Any dispute, claim, or controversy arising out of or relating to these Terms or the Service shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association (AAA), rather than in court, except that either party may seek injunctive relief in court for intellectual property violations.
            </Paragraph>
            <SubTitle>12.3 Class Action Waiver</SubTitle>
            <Paragraph className="uppercase text-sm font-semibold text-gray-300">
              YOU AGREE THAT ANY DISPUTE RESOLUTION PROCEEDINGS WILL BE CONDUCTED ON AN INDIVIDUAL BASIS AND NOT AS PART OF A CLASS, CONSOLIDATED, OR REPRESENTATIVE ACTION.
            </Paragraph>

            <SectionTitle number="13">Modifications to Terms</SectionTitle>
            <Paragraph>
              We reserve the right to modify these Terms at any time. We will provide notice of material changes by updating the "Last Updated" date at the top of this page and, where appropriate, notifying you via email or through the platform. Your continued use of the Service after any changes constitutes your acceptance of the revised Terms.
            </Paragraph>

            <SectionTitle number="14">Termination</SectionTitle>
            <Paragraph>
              We may suspend or terminate your access to the Service at any time, with or without cause or notice, including for violation of these Terms. Upon termination:
            </Paragraph>
            <BulletList items={[
              "Your right to access and use the Service will immediately cease.",
              "Any outstanding subscription fees remain payable.",
              "Provisions that by their nature should survive termination will remain in effect."
            ]} />

            <SectionTitle number="15">Entire Agreement</SectionTitle>
            <Paragraph>
              These Terms, together with our Privacy Policy and any other agreements expressly incorporated by reference, constitute the entire agreement between you and ProfitLense.AI with respect to the Service and supersede all prior agreements, understandings, or representations.
            </Paragraph>

            <SectionTitle number="16">Contact Us</SectionTitle>
            <Paragraph>
              If you have any questions, concerns, or requests regarding these Terms, please contact us:
            </Paragraph>
            <div className="bg-gray-800/30 border border-gray-700/50 rounded-xl p-6 mt-4 inline-block min-w-[250px]">
              <p className="text-white font-semibold mb-2 flex items-center gap-2">
                <SafeIcon icon={FiShield} className="text-blue-500" />
                ProfitLense.AI
              </p>
              <p className="text-gray-400 mb-1">
                <span className="text-gray-500 w-16 inline-block">Email:</span> 
                <a href="mailto:support@profitlense.ai" className="text-blue-400 hover:text-blue-300">support@profitlense.ai</a>
              </p>
              <p className="text-gray-400">
                <span className="text-gray-500 w-16 inline-block">Website:</span> 
                <a href="https://www.profitlense.ai" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">www.profitlense.ai</a>
              </p>
            </div>

            <div className="mt-16 pt-8 border-t border-gray-800/60 text-center">
              <p className="text-gray-500 italic">
                By using ProfitLense.AI, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
              </p>
            </div>

          </div>
        </motion.div>
      </div>
    </div>
  );
}